import capitalizeText from 'lodash/capitalize';
import startCase from 'lodash/startCase';

export const titleCase = (str: string) => {
  if (!str) {
    return str;
  }
  return str
    .split(` `)
    .map(word => startCase(word.toLowerCase()))
    .join(` `);
};

export const capitalize = (str: string) => {
  if (!str) {
    return str;
  }
  return str
    .split(` `)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(` `);
};

export const toKebabCase = (value: string) =>
  value
    .replace(/([A-Z])([A-Z])/g, `$1-$2`)
    .replace(/([a-z])([A-Z])/g, `$1-$2`)
    .replace(/[\s_]+/g, `-`)
    .toLowerCase();

/**
 * Formats an enum string to a human-readable format using start case (Title Case).
 * Converts underscores and camelCase to spaces and capitalizes each word.
 *
 * @param enumStr - The enum string to format
 * @param preserveCasing - Whether to preserve the original casing (default: false)
 * @returns Formatted string in Title Case or undefined if input is undefined
 *
 * @example
 * formatEnum('USER_ROLE') // 'User Role'
 * formatEnum('userRole') // 'User Role'
 * formatEnum('ADMIN_SUPER_USER') // 'Admin Super User'
 * formatEnum('USER_ROLE', true) // 'USER ROLE'
 */
export const formatEnum = (enumStr: string | undefined, preserveCasing: boolean = false) => {
  return enumStr ? (preserveCasing ? startCase(enumStr) : startCase(enumStr.toLowerCase())) : undefined;
};

/**
 * Formats an enum string to sentence case format.
 * Replaces underscores with spaces, converts to lowercase, then capitalizes only the first letter.
 *
 * @param enumStr - The enum string to format
 * @returns Formatted string in sentence case or undefined if input is undefined
 *
 * @example
 * formatEnumSentenceCase('USER_ROLE') // 'User role'
 * formatEnumSentenceCase('ADMIN_SUPER_USER') // 'Admin super user'
 * formatEnumSentenceCase('PENDING_APPROVAL') // 'Pending approval'
 */
export const formatEnumSentenceCase = (enumStr: string | undefined) => {
  return enumStr ? capitalizeText(enumStr.replace(/_/g, ` `).toLowerCase()) : undefined;
};

/**
 * Returns the singular or plural form of a word based on the count, using the common English rules
 * (y→ies, s/x/z/ch/sh→es, otherwise +s).
 *
 * @example
 * pluralize(1, 'day') // 'day'
 * pluralize(3, 'day') // 'days'
 * pluralize(2, 'reason') // 'reasons'
 * pluralize(2, 'category') // 'categories'
 */
export const pluralize = (count: number, word: string): string => {
  if (count === 1) {
    return word;
  }
  if (word.endsWith(`y`) && !/[aeiou]y$/.test(word)) {
    return `${word.slice(0, -1)}ies`;
  }
  if (/(s|x|z|ch|sh)$/.test(word)) {
    return `${word}es`;
  }
  return `${word}s`;
};
